import { defineStore } from 'pinia';
import { ref } from 'vue';
import axiosInstance from '@/shared/utils/axios';
import { Equipment } from '@/types/types';

export interface EquipmentData {
  equipment: Equipment[];
  totalCount: number;
  page: number;
  limit: number;
}

export const useEquipmentStore = defineStore('equipment', () => {
  const equipmentData = ref<EquipmentData>({
    equipment: [],
    totalCount: 0,
    page: 1,
    limit: 10
  });

  const fetchEquipment = async (page: number = 1, limit: number = 10, search: string = "", type: string = "") => {
    try {
      const response = await axiosInstance.get('/api/equipment', {
        params: { page, limit, search, type }
      });
      equipmentData.value = {
        equipment: response.data.equipment,
        totalCount: response.data.pagination.totalCount,
        page: response.data.pagination.page,
        limit: response.data.pagination.limit
      };
      return response.data;
    } catch (error) {
      console.error('Error fetching equipment:', error);
      throw error;
    }
  };

  const addEquipment = async (equipmentItem: Partial<Equipment>) => {
    try {
      const response = await axiosInstance.post('/api/equipment', equipmentItem);
      const { equipment: newEquipment, equipmentList, pagination } = response.data;
      equipmentData.value = {
        equipment: equipmentList,
        totalCount: pagination.totalCount,
        page: pagination.page,
        limit: pagination.limit
      };
      return newEquipment;
    } catch (error) {
      console.error('Error adding equipment:', error);
      throw error;
    }
  };

  const updateEquipment = async (equipmentItem: Equipment) => {
    try {
      if (!equipmentItem || !equipmentItem._id) {
        throw new Error('Invalid equipment data: Missing _id');
      }
      const response = await axiosInstance.put(`/api/equipment/${equipmentItem._id}`, equipmentItem);
      const { equipment: updatedEquipment, equipmentList, pagination } = response.data;
      if (!updatedEquipment || !updatedEquipment._id) {
        throw new Error('Invalid response: Updated equipment data is missing or incomplete');
      }
      equipmentData.value = {
        equipment: equipmentList,
        totalCount: pagination.totalCount,
        page: pagination.page,
        limit: pagination.limit
      };
      return updatedEquipment;
    } catch (error) {
      console.error('Error updating equipment:', error);
      throw error;
    }
  };

  const getEquipmentById = async (id: string) => {
    try {
      const response = await axiosInstance.get(`/api/equipment/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching equipment by ID:', error);
      throw error;
    }
  };

  const deleteEquipment = async (id: string) => {
    try {
      await axiosInstance.delete(`/api/equipment/${id}`);
      equipmentData.value.equipment = equipmentData.value.equipment.filter(item => item._id !== id);
      equipmentData.value.totalCount--;
    } catch (error) {
      console.error('Error deleting equipment:', error);
      throw error;
    }
  };

  return {
    equipmentData,
    fetchEquipment,
    addEquipment,
    updateEquipment,
    getEquipmentById,
    deleteEquipment
  };
});
